import { Select } from "antd"
import type { BaseOptionType, SelectProps } from "antd/es/select"
import React, { useMemo } from "react"

import { hideEmptyOverflow } from "../../utils/select/max-tag-placeholder.tsx"
import {
  type FallbackLabels,
  mergeSelectedIntoOptions,
  modeAcceptsFreeText
} from "../../utils/select/resolve-options.ts"

export interface ResolvedSelectProps<
  ValueType = any,
  OptionType extends BaseOptionType = BaseOptionType
> extends SelectProps<ValueType, OptionType> {
  fallbackLabels?: FallbackLabels
  fallbackLabel?: string
}

/**
 * antd's Select with one guarantee added: a selected value the options list does
 * not cover renders a label, never its raw id. Everything else passes straight
 * through, so it is a drop-in for `Select` from antd.
 */
function ResolvedSelectInner<
  ValueType = any,
  OptionType extends BaseOptionType = BaseOptionType
>(
  {
    options,
    value,
    fallbackLabels,
    fallbackLabel,
    ...props
  }: ResolvedSelectProps<ValueType, OptionType>,
  ref: React.Ref<any>
) {
  // A Select driven by <Option> children passes no options at all; forcing an
  // array on it would blank the dropdown.
  const resolvedOptions = useMemo(
    () =>
      options && !modeAcceptsFreeText(props.mode)
        ? mergeSelectedIntoOptions(
            options,
            value,
            fallbackLabels,
            fallbackLabel
          )
        : options,
    [options, value, fallbackLabels, fallbackLabel, props.mode]
  )

  return (
    <Select
      ref={ref}
      value={value}
      maxTagPlaceholder={hideEmptyOverflow}
      {...(resolvedOptions
        ? { options: resolvedOptions as unknown as OptionType[] }
        : {})}
      {...props}
    />
  )
}

export const ResolvedSelect = React.forwardRef(ResolvedSelectInner) as <
  ValueType = any,
  OptionType extends BaseOptionType = BaseOptionType
>(
  props: ResolvedSelectProps<ValueType, OptionType> & {
    ref?: React.Ref<any>
  }
) => React.ReactElement

export default ResolvedSelect
